import { ExternalLink } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { SettingCell } from '@/components/setting-cell'
import { formatMoney, formatPercent, parseUTCDate, cn } from '@/lib/utils'
import { sellerName } from '@/lib/retailers'
import type { components } from '@/api/schema'

type PricingDetail = components['schemas']['PricingDetailRead']
type Decision = components['schemas']['PriceDecisionSummaryRead']
type Special = components['schemas']['PriceSpecialRead']
type RetailerNet = components['schemas']['RetailerNetRead']
type Offer = components['schemas']['CompetitorOfferRead']

interface PricingDetailViewProps {
  detail: PricingDetail | undefined
  isLoading: boolean
}

function shortDate(value: string): string {
  const date = /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? new Date(`${value}T12:00:00Z`)
    : parseUTCDate(value)
  const sameYear = date.getFullYear() === new Date().getFullYear()
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
    ...(sameYear ? {} : { year: 'numeric' }),
  })
}

function num(value: string | null | undefined): number | null {
  if (value == null) return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

function percent(share: string | number): string {
  return formatPercent(Number(share) * 100).replace('.0%', '%')
}

/** A retailer's price as the reason quotes it: per each when we sell by the case. */
function quoted(offer: { each?: string | null; price?: string | null }, sellingEach: number): string {
  if (sellingEach > 1 && offer.each != null) return `${formatMoney(offer.each)} each`
  return formatMoney(offer.price)
}

/** A retailer's price on our selling unit: per each when we sell by the each, else per our case. */
function onOurUnit(offer: { each?: string | null; price: string }, sellingEach: number): number {
  const each = num(offer.each)
  return each != null ? each * sellingEach : Number(offer.price)
}

export function PricingDetailView({ detail, isLoading }: PricingDetailViewProps) {
  if (isLoading) {
    return <Skeleton className="h-48 w-full" />
  }
  if (!detail || (!detail.decision && detail.offers.length === 0 && !detail.custom_pricing)) {
    return null
  }
  const decision = detail.decision

  return (
    <section aria-labelledby="price-reason" className="overflow-hidden rounded-xl border bg-card">
      <div className="flex items-baseline justify-between gap-3 border-b px-4 py-3">
        <h2 id="price-reason" className="text-sm font-semibold">
          Why this price
        </h2>
        {decision && (
          <p className="text-xs text-muted-foreground">Since {shortDate(decision.valid_from)}</p>
        )}
      </div>
      {!decision && detail.custom_pricing && (
        <p className="border-b px-4 py-3 text-sm" data-testid="price-reason-text">
          Priced on each quote.
        </p>
      )}
      {decision && (
        <>
          {detail.retailer_net && (
            <p className="border-b px-4 py-3 text-sm" data-testid="price-net-text">
              {retailerNetText(detail.retailer_net, decision)}
            </p>
          )}
          <p className="border-b px-4 py-3 text-sm" data-testid="price-reason-text">
            {reasonText(detail, decision)}
          </p>
          {detail.special && (
            <p className="border-b px-4 py-3 text-sm" data-testid="price-special-text">
              {specialText(detail.special, decision)}
            </p>
          )}
          <div
            className={cn(
              '-mb-px -mr-px grid',
              decision.floor_price != null && decision.ceiling_price != null && 'grid-cols-2',
            )}
          >
            {decision.floor_price != null && (
              <SettingCell label={floorLabel(detail, decision)}>
                <span className="text-sm font-medium tabular-nums">
                  {formatMoney(decision.floor_price)}
                </span>
              </SettingCell>
            )}
            {decision.ceiling_price != null && (
              <SettingCell label="Normal markup">
                <span className="text-sm font-medium tabular-nums">
                  {formatMoney(decision.ceiling_price)}
                </span>
              </SettingCell>
            )}
          </div>
        </>
      )}
      {detail.offers.length > 0 && <OffersTable detail={detail} />}
    </section>
  )
}

/** "NET $42.75 from PCR 2026 Conference Special (extra 5%), until Oct 30." */
function specialText(special: Special, decision: Decision): string {
  const term = special.extra_discount
    ? `extra ${special.extra_discount.split('/').join('% + ')}%`
    : 'special NET'
  const net = decision.effective_net != null ? `NET ${formatMoney(decision.effective_net)}` : 'NET'
  return `${net} from ${special.program} (${term}), until ${shortDate(special.ends_on)}.`
}

/** "NET $386.99 is Webstaurant's price, seen Oct 8." */
function retailerNetText(net: RetailerNet, decision: Decision): string {
  const amount = formatMoney(decision.effective_net ?? net.price)
  return `NET ${amount} is ${sellerName(net.seller)}'s price, seen ${shortDate(net.observed)}.`
}

function floorLabel(detail: PricingDetail, decision: Decision): string {
  const map = num(decision.effective_map)
  if (map != null && map === num(decision.floor_price)) return 'Floor (MAP)'
  return `Floor (${percent(detail.floor_margin)} margin)`
}

const BASE_REASON: Record<string, string> = {
  map: 'At MAP.',
  aq_markup: 'Our normal markup on NET.',
  floor: 'At our floor.',
  manual: 'Set by hand.',
  minimum_margin: 'Held at the manufacturer minimum margin.',
  none: 'No price.',
}

function reasonText(detail: PricingDetail, decision: Decision): string {
  const reason = detail.competitor
  const base = BASE_REASON[decision.store_source] ?? 'Automatic price.'
  if (!reason) return base
  const sellingEach = Number(detail.selling_each)
  const seller = sellerName(reason.seller)
  const theirs = quoted(reason, sellingEach)
  const seen = reason.observed ? `, seen ${shortDate(reason.observed)}` : ''
  switch (reason.outcome) {
    case 'matched_lower':
    case 'matched_higher':
      return `1% under ${seller}'s ${theirs}${seen}.`
    case 'within_stability':
      return `${base} 1% under ${seller}'s ${theirs} is too small a change to move it.`
    case 'capped_at_ceiling':
      return `${base} ${seller} sells higher, at ${theirs}.`
    case 'blocked_by_floor':
      return `At our ${percent(detail.floor_margin)} floor. ${seller} sells at ${theirs}${
        reason.net_to_match != null
          ? `; matching it needs NET of ${formatMoney(reason.net_to_match)} or less.`
          : '.'
      }`
    case 'kept_at_map': {
      const map = num(decision.effective_map)
      const below = detail.offers
        .filter((offer) => offer.status === 'below_map')
        .map((offer) => ({ offer, price: onOurUnit(offer, sellingEach) }))
        .sort((a, b) => a.price - b.price)[0]
      if (below && map != null) {
        return `Kept at MAP. ${sellerName(below.offer.seller)} sells ${formatMoney(map - below.price)} below MAP.`
      }
      return 'At MAP. No retailer sells below it.'
    }
    case 'outside_band':
      return `${base} Retailer prices are too far from ours to compare.`
    case 'unit_unknown':
      return `${base} Retailers list no pack size, so they can't be compared.`
    default:
      return base
  }
}

const STATUS: Record<string, { label: string; tone: 'lead' | 'warn' | 'muted' | 'plain' }> = {
  sets_price: { label: 'Sets our price', tone: 'lead' },
  below_floor: { label: 'Below our floor', tone: 'warn' },
  below_map: { label: 'Below MAP', tone: 'warn' },
  at_or_above_map: { label: 'At or above MAP', tone: 'plain' },
  set_aside: { label: 'Set aside', tone: 'muted' },
  higher: { label: 'Higher', tone: 'plain' },
  outside_band: { label: 'Not comparable', tone: 'muted' },
  unit_unknown: { label: 'Pack size unknown', tone: 'muted' },
  out_of_stock: { label: 'Out of stock', tone: 'muted' },
}

function statusDetail(offer: Offer, detail: PricingDetail): string | null {
  const sellingEach = Number(detail.selling_each)
  const decision = detail.decision
  const ratio = num(offer.ratio)
  switch (offer.status) {
    case 'sets_price':
      if (detail.competitor?.outcome === 'capped_at_ceiling') return 'Capped at our normal markup'
      if (detail.competitor?.outcome === 'within_stability') return 'Change too small to move our price'
      return 'Our price is 1% under this'
    case 'below_floor':
      return `1% under is ${formatMoney(onOurUnit(offer, sellingEach) * 0.99)}, below our floor of ${formatMoney(decision?.floor_price)}`
    case 'below_map': {
      const map = num(decision?.effective_map)
      return map != null ? `${formatMoney(map - onOurUnit(offer, sellingEach))} under MAP` : null
    }
    case 'set_aside':
      return 'Retailers differ by over 30%, so the middle price is used'
    case 'outside_band':
      if (ratio == null) return null
      return ratio < 1
        ? `${Math.round((1 - ratio) * 100)}% below ours, likely another pack or model`
        : `${Math.round((ratio - 1) * 100)}% above ours, likely another pack or model`
    case 'unit_unknown':
      return 'We sell by the case and this listing gives no pack size'
    default:
      return null
  }
}

function OffersTable({ detail }: { detail: PricingDetail }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="pl-4">Retailer</TableHead>
          <TableHead className="pr-4 text-right sm:pr-2">Price</TableHead>
          <TableHead className="hidden text-right sm:table-cell">Seen</TableHead>
          <TableHead className="hidden pr-4 sm:table-cell">Status</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {detail.offers.map((offer) => (
          <OfferRow key={offer.observation_id} offer={offer} detail={detail} />
        ))}
      </TableBody>
    </Table>
  )
}

function OfferRow({ offer, detail }: { offer: Offer; detail: PricingDetail }) {
  const status = STATUS[offer.status] ?? { label: offer.status, tone: 'muted' as const }
  const pack = num(offer.pack)
  const tip = statusDetail(offer, detail)
  const tone = cn(
    status.tone === 'lead' && 'font-semibold',
    status.tone === 'warn' && 'font-medium text-destructive',
    status.tone === 'muted' && 'text-muted-foreground',
  )
  const label = <span className={cn('text-sm', tone)}>{status.label}</span>
  return (
    <TableRow className={cn(offer.chosen && 'bg-muted/40')}>
      <TableCell className="pl-4">
        {offer.url ? (
          // eslint-disable-next-line no-restricted-syntax -- external retailer listing
          <a
            href={offer.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 font-medium hover:underline"
          >
            {sellerName(offer.seller)}
            <ExternalLink className="h-3 w-3 text-muted-foreground" aria-hidden />
          </a>
        ) : (
          <span className="font-medium">{sellerName(offer.seller)}</span>
        )}
        <p className="text-xs text-muted-foreground sm:hidden">
          {shortDate(offer.observed_at)} · <span className={tone}>{status.label}</span>
        </p>
      </TableCell>
      <TableCell className="pr-4 text-right tabular-nums sm:pr-2">
        <p className="font-medium">{formatMoney(offer.price)}</p>
        {pack != null && pack > 1 && (
          <p className="text-xs text-muted-foreground">
            {pack} pack · {formatMoney(offer.each)} each
          </p>
        )}
      </TableCell>
      <TableCell className="hidden text-right text-muted-foreground sm:table-cell">
        {shortDate(offer.observed_at)}
      </TableCell>
      <TableCell className="hidden pr-4 sm:table-cell">
        {tip ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="cursor-help underline decoration-dotted underline-offset-4"
              >
                {label}
              </button>
            </TooltipTrigger>
            <TooltipContent>{tip}</TooltipContent>
          </Tooltip>
        ) : (
          label
        )}
      </TableCell>
    </TableRow>
  )
}
